/**
 * Import Language Selector Component
 * Dropdown for selecting a language code for single-language JSON imports
 * Shows only languages configured for the selected application type
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import { Label } from '@/core/components/ui/label';
import { useApplicationLanguagesStore } from '../../stores/application-languages-store';
import { useTranslationStore } from '../../stores/translation-store';
import { useEffect } from 'react';

interface ImportLanguageSelectorProps {
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

export function ImportLanguageSelector({
  value,
  onChange,
  disabled = false,
  className = '',
}: ImportLanguageSelectorProps) {
  const { selectedAppType } = useTranslationStore();
  const { applicationLanguages, fetchApplicationLanguages, isLoading } = useApplicationLanguagesStore();

  // Fetch languages for the selected app type
  useEffect(() => {
    if (selectedAppType) {
      fetchApplicationLanguages(selectedAppType);
    }
  }, [selectedAppType, fetchApplicationLanguages]);

  // Build language options from application-specific languages
  const languageOptions = applicationLanguages.map((lang) => ({
    code: lang.languageCode,
    name: lang.languageName || lang.nativeName || lang.languageCode,
  }));

  // Sort alphabetically by name
  languageOptions.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className={`space-y-2 ${className}`}>
      <Label>Language Code</Label>
      <Select
        value={value || ''}
        onValueChange={onChange}
        disabled={disabled || isLoading || languageOptions.length === 0}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select language for this import" />
        </SelectTrigger>
        <SelectContent>
          {languageOptions.map((lang) => (
            <SelectItem key={lang.code} value={lang.code}>
              <span>{lang.name}</span>
              <span className="ml-2 text-xs text-muted-foreground">({lang.code})</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        This JSON file contains translations for a single language.
        Select which language code these translations belong to.
      </p>
    </div>
  );
}
